import type { Journey, TestSessionBug, TestSessionJourneyRun } from '../data/projectTypes'

export type JourneyStateGroupId = 'running' | 'queued' | 'bugs-reported' | 'attention' | 'passed' | 'skipped' | 'not-run'

export interface JourneyStateGroupDefinition {
  id: JourneyStateGroupId
  label: string
  description: string
}

export const JOURNEY_STATE_GROUPS: JourneyStateGroupDefinition[] = [
  { id: 'bugs-reported', label: 'With Bugs', description: 'Journeys that reported one or more bugs' },
  { id: 'running', label: 'Running now', description: 'Journeys actively being tested' },
  { id: 'queued', label: 'Queued', description: 'Journeys waiting for a worker' },
  { id: 'passed', label: 'Passed', description: 'Journeys whose latest run completed successfully' },
  { id: 'skipped', label: 'Skipped', description: 'Journeys intentionally removed from this Test Run' },
  { id: 'not-run', label: 'Not run', description: 'Journeys without a test result yet' },
  { id: 'attention', label: 'Failed', description: 'Journeys that failed, were blocked, or could not finish' },
]

const ATTENTION_STATUSES = new Set(['failed', 'infra-failed', 'incomplete', 'blocked'])

export function journeyStateGroup(status: string | null | undefined): JourneyStateGroupId {
  if (status === 'in-progress') return 'running'
  if (status === 'queued') return 'queued'
  if (status === 'completed') return 'passed'
  if (status === 'continued' || status === 'cancelled') return 'skipped'
  if (status && ATTENTION_STATUSES.has(status)) return 'attention'
  return status ? 'attention' : 'not-run'
}

export function groupJourneysByState(journeys: Journey[]): Record<JourneyStateGroupId, Journey[]> {
  const grouped: Record<JourneyStateGroupId, Journey[]> = {
    running: [],
    queued: [],
    'bugs-reported': [],
    attention: [],
    passed: [],
    skipped: [],
    'not-run': [],
  }
  for (const journey of journeys) grouped[journeyStateGroup(journey.last_run_status)].push(journey)
  return grouped
}

export function testSessionJourneyRunStateGroup(
  run: TestSessionJourneyRun,
  hasAssociatedBugs = false,
): JourneyStateGroupId {
  if (run.bugs_found > 0 || hasAssociatedBugs) return 'bugs-reported'
  return journeyStateGroup(run.status)
}

export function groupTestSessionJourneyRunsByState(
  runs: TestSessionJourneyRun[],
  bugs: TestSessionBug[] = [],
): Record<JourneyStateGroupId, TestSessionJourneyRun[]> {
  const grouped: Record<JourneyStateGroupId, TestSessionJourneyRun[]> = {
    running: [],
    queued: [],
    'bugs-reported': [],
    attention: [],
    passed: [],
    skipped: [],
    'not-run': [],
  }
  const bugsByRunId = groupTestSessionBugsByJourneyRun(bugs, runs)
  for (const run of runs) {
    grouped[testSessionJourneyRunStateGroup(run, bugsByRunId.has(run.id))].push(run)
  }
  return grouped
}

export function groupTestSessionBugsByRunId(bugs: TestSessionBug[]): Map<string, TestSessionBug[]> {
  const grouped = new Map<string, TestSessionBug[]>()
  for (const bug of bugs) {
    if (!bug.test_run_id) continue
    const runBugs = grouped.get(bug.test_run_id) ?? []
    if (!runBugs.some(existing => existing.id === bug.id)) runBugs.push(bug)
    grouped.set(bug.test_run_id, runBugs)
  }
  return grouped
}

export function groupTestSessionBugsByJourneyRun(
  bugs: TestSessionBug[],
  runs: TestSessionJourneyRun[],
): Map<string, TestSessionBug[]> {
  const representativeByAttemptId = new Map<string, string>()
  for (const run of runs) {
    for (const attemptId of run.attempt_ids) representativeByAttemptId.set(attemptId, run.id)
  }
  const grouped = new Map<string, TestSessionBug[]>()
  for (const bug of bugs) {
    if (!bug.test_run_id) continue
    const representativeId = representativeByAttemptId.get(bug.test_run_id)
    if (!representativeId) continue
    const runBugs = grouped.get(representativeId) ?? []
    if (!runBugs.some(existing => existing.id === bug.id)) runBugs.push(bug)
    grouped.set(representativeId, runBugs)
  }
  return grouped
}

export function isPendingJourneyRun(status: string): boolean {
  return status === 'queued' || status === 'in-progress'
}

export interface TestRunStoryMetrics {
  finished: number
  total: number
  passes: number
  failures: number
}

export function getTestRunStoryMetrics(runs: TestSessionJourneyRun[]): TestRunStoryMetrics {
  let finished = 0
  let passes = 0
  let failures = 0
  for (const run of runs) {
    if (!isPendingJourneyRun(run.status)) finished++
    if (run.status === 'completed') passes++
    if (ATTENTION_STATUSES.has(run.status)) failures++
  }
  return { finished, total: runs.length, passes, failures }
}
